import { useEffect, useRef } from 'react'
import Masonry from 'react-masonry-css'
import cc from 'classcat'

import { useTranslate } from '../../../utils/translate'
import { useUnsplash } from '../UnsplashImageSearchContext'

export function UnsplashImageSearchResults() {
  const t = useTranslate('interface', 'components.imageUploadSelectionComponent.UnsplashImageSearchPopover')
  const {
    searchResults: images,
    isLoading,
    selectedImage,
    activeCategory,
    isNewSearch,
    isDownloading,
    shouldShowLoadMoreButton,
    setSelectedImage,
    loadMoreImages,
    getLazyLoadRef,
  } = useUnsplash()

  const resultsContainerRef = useRef<HTMLDivElement>(null)

  // Reset scroll position when activeCategory or search results change
  useEffect(() => {
    if (resultsContainerRef.current) {
      resultsContainerRef.current.scrollTop = 0
    }
  }, [activeCategory, isNewSearch])

  const showNoResultsMessage = !isLoading && images.length === 0

  const renderContent = () => {
    // Show loading spinner for initial load or when changing categories (new search)
    if (isLoading && (images.length === 0 || isNewSearch)) {
      return <span data-testid="loading-spinner" className="ep-image-editor-loading-spinner" />
    }

    // No search results
    if (showNoResultsMessage) {
      return (
        <div className="unsplash-search-results-empty">
          <h4 className="unsplash-search-results-empty-title">{t('noSearchResults.headline')}</h4>
          <p className="unsplash-search-results-empty-description">{t('noSearchResults.description')}</p>
        </div>
      )
    }

    return (
      <>
        <Masonry breakpointCols={3} className="masonry-grid" columnClassName="masonry-grid_column">
          {images.map((image, index) => (
            <button
              key={image.id}
              style={{ '--item-delay': index % 12 }}
              className={cc([
                'unsplash-search-results-grid-item',
                selectedImage?.id === image.id && 'unsplash-search-results-grid-item-selected',
              ])}
              onClick={() => setSelectedImage(image)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setSelectedImage(image)
                }
              }}
              tabIndex={0}
              ref={getLazyLoadRef(index)}
            >
              {selectedImage?.id === image.id && (
                <span className="unsplash-search-results-grid-item-selected-label">{t('imageSelectedLabel')}</span>
              )}
              <img
                className="unsplash-search-results-grid-item-image"
                src={image.urls.small}
                alt={image.alt_description || ''}
                width={image.width}
                height={image.height}
                loading="lazy"
              />
            </button>
          ))}
        </Masonry>

        {shouldShowLoadMoreButton && !isLoading && (
          <div className="unsplash-search-load-more" style={{ textAlign: 'center', marginTop: '15px' }}>
            <button type="button" className="unsplash-search-load-more-button" onClick={loadMoreImages}>
              {t('loadmoreButton.label')}
            </button>
          </div>
        )}

        {isLoading && images.length > 0 && (
          <div className="unsplash-search-results-loading-more">{t('loadingState.description')}</div>
        )}
      </>
    )
  }

  return (
    <scroll-shadow>
      {isDownloading && (
        <div className="unsplash-search-results-overlay" data-testid="unsplash-search-results-overlay" />
      )}
      <div ref={resultsContainerRef} className={'unsplash-search-results'}>
        {isDownloading && <span className="ep-image-editor-loading-spinner" data-testid="loading-spinner" />}
        {renderContent()}
      </div>
    </scroll-shadow>
  )
}
